import * as C from "../const";
import { getAccessToken, getAccessTokenExpiry } from "./tokenService";

const authChannel =
  typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined"
    ? new window.BroadcastChannel(C.AUTH_BROADCAST_CHANNEL_NAME)
    : null;

// --- Cross-tab freshness marker: only the expiry timestamp is shared, never the token itself ---
const readSharedTokenExpiry = () => {
  const raw = localStorage.getItem(C.AUTH_TOKEN_EXPIRY_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

const writeSharedTokenExpiry = (expiresAt) => {
  localStorage.setItem(C.AUTH_TOKEN_EXPIRY_STORAGE_KEY, String(expiresAt));
};

export const clearSharedTokenExpiry = () => {
  localStorage.removeItem(C.AUTH_TOKEN_EXPIRY_STORAGE_KEY);
};

const isSharedTokenFresh = (bufferSeconds) => {
  const expiresAt = readSharedTokenExpiry();
  return !!expiresAt && expiresAt - Date.now() > bufferSeconds * 1000;
};

/** Guards against forwarding a malformed/stale same-origin message as a real token update. */
const isValidTokenPayload = (payload) => {
  const { accessToken, expiresAt } = payload || {};
  return typeof accessToken === "string" && accessToken.length > 0 &&
    Number.isFinite(expiresAt) && expiresAt > Date.now();
};

/** Resolves with a peer tab's token once it broadcasts a valid TOKEN_UPDATED, or null on timeout. */
export const waitForPeerToken = (timeoutMs = C.PEER_SYNC_WAIT_MS) => {
  if (!authChannel) return Promise.resolve(null);

  return new Promise((resolve) => {
    const cleanup = () => {
      clearTimeout(timer);
      authChannel.removeEventListener("message", onMessage);
    };
    const onMessage = (event) => {
      if (event?.data?.type === "TOKEN_UPDATED" && isValidTokenPayload(event.data.payload)) {
        cleanup();
        resolve(event.data.payload.accessToken);
      }
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    authChannel.addEventListener("message", onMessage);
  });
};

// Publishers
export const broadcastTokenRequest = () => {
  console.log(`[Auth Channel: ${new Date().toISOString()}] Broadcasting REQUEST_TOKEN`);
  authChannel?.postMessage({ type: "REQUEST_TOKEN" });
};

export const broadcastTokenUpdated = (accessToken, expiresAt) => {
  console.log(`[Auth Channel: ${new Date().toISOString()}] Broadcasting TOKEN_UPDATED`);
  writeSharedTokenExpiry(expiresAt);
  authChannel?.postMessage({ type: "TOKEN_UPDATED", payload: { accessToken, expiresAt } });
};

export const broadcastTokenCleared = () => {
  console.log(`[Auth Channel: ${new Date().toISOString()}] Broadcasting TOKEN_CLEARED`);
  clearSharedTokenExpiry();
  authChannel?.postMessage({ type: "TOKEN_CLEARED" });
};

export const broadcastProjectSwitched = (clientId) => {
  console.log(`[Auth Channel: ${new Date().toISOString()}] Broadcasting PROJECT_SWITCHED to clientId: ${clientId}`);
  authChannel?.postMessage({ type: "PROJECT_SWITCHED", payload: { clientId } });
};

/**
 * Subscriber: Accepts semantic callback handlers.
 * Isolates all message routing, string-checking, and event parsing.
 */
export const subscribeToAuthBroadcasts = (handlers = {}) => {
  if (!authChannel) return () => {};

  const required = ["onTokenRequested", "onTokenUpdated", "onTokenCleared", "onProjectSwitched"];
  const missing = required.filter((h) => typeof handlers[h] !== "function");
  if (missing.length > 0) {
    throw new Error(
      `[Auth Broadcast] Missing required callback handlers: ${missing.join(", ")}. All critical synchronization handlers must be defined.`
    );
  }

  const handleMessage = (event) => {
    const { type, payload } = event?.data || {};

    console.log(`[Auth Channel: ${new Date().toISOString()}] Received broadcast: ${type}`);

    if (type === "REQUEST_TOKEN") {
      handlers.onTokenRequested();
    } else if (type === "TOKEN_UPDATED" && isValidTokenPayload(payload)) {
      handlers.onTokenUpdated(payload);
    } else if (type === "TOKEN_CLEARED") {
      handlers.onTokenCleared();
    } else if (type === "PROJECT_SWITCHED") {
      handlers.onProjectSwitched(payload);
    }
  };

  authChannel.addEventListener("message", handleMessage);

  return () => {
    authChannel.removeEventListener("message", handleMessage);
  };
};

/**
 * Reuses an already-refreshed token instead of hitting the network, but only once the local
 * in-memory token's own expiry is proven to match or exceed the shared marker - an unconfirmed
 * in-memory token may still be the pre-refresh value if this tab hasn't processed the leader's
 * broadcast yet, so we wait for that broadcast rather than trusting it blindly.
 */
const reuseFreshTokenOrNull = async (bufferSeconds, peerWaitMs) => {
  if (!isSharedTokenFresh(bufferSeconds)) {
    return null;
  }

  const sharedExpiresAt = readSharedTokenExpiry();
  const localExpiresAt = getAccessTokenExpiry();
  const localToken = getAccessToken();
  if (localToken && localExpiresAt && localExpiresAt >= sharedExpiresAt) {
    return localToken;
  }

  return waitForPeerToken(peerWaitMs);
};

/**
 * Serializes `executeNetworkRefresh` across tabs via a Web Lock so only one tab ever
 * calls the refresh endpoint at a time; queued tabs reuse the winner's broadcast token instead.
 * Without the Web Locks API, true mutual exclusion isn't possible - the freshness/peer-wait
 * reuse check below still runs to avoid most duplicate calls, but a race can still occur.
 */
export const coordinateRefresh = async (executeNetworkRefresh, options = {}) => {
  const {
    bufferSeconds = C.AUTH_REFRESH_BUFFER_SECONDS,
    peerWaitMs = C.PEER_SYNC_WAIT_MS,
    allowPeerReuse = true,
  } = options;

  const attempt = async () => {
    if (allowPeerReuse) {
      const reused = await reuseFreshTokenOrNull(bufferSeconds, peerWaitMs);
      if (reused) return reused;
    }
    return executeNetworkRefresh();
  };

  if (typeof navigator === "undefined" || !navigator.locks) {
    return attempt();
  }

  return navigator.locks.request(C.AUTH_LOCK_NAME, attempt);
};
